<template>
  <div>
    <a
      :href="viewUrl || '#'"
      :data-test-id="`${dataTestId}-view-btn-${rowIndex}`"
      @click.prevent="!disabled && $emit('actionView')"
      :class="[
        disabled
          ? 'tw-inline-flex tw-opacity-50 tw-cursor-not-allowed'
          : 'tw-inline-flex',
      ]"
    >
      <cl-table-action-button
        :data-test-id="`${dataTestId}-view-action-btn-${rowIndex}`"
        :readonly="disabled"
        :class="[disabled ? 'tw-inline-flex tw-opacity-50' : 'tw-inline-flex']"
      >
        <cb-icon-view :width="18" />
      </cl-table-action-button>
    </a>
    <authorisation-wrapper
      v-if="!embedded"
      :resource="ACCOUNTS"
      :action="WRITE"
      v-slot="{ isAllowed: isWriteAllowed }"
    >
      <cl-table-action-button
        :data-test-id="`${dataTestId}-edit-btn-${rowIndex}`"
        :readonly="!isWriteAllowed || disabled"
        :class="[disabled ? 'tw-inline-flex tw-opacity-50' : 'tw-inline-flex']"
        @on-click="onEditClick"
      >
        <cl-spinner
          :loading-text="$t('loading')"
          v-if="loading"
          :size="'small'"
          class="mr-2"
        />
        <cb-icon-edit v-else :width="18" />
      </cl-table-action-button>
    </authorisation-wrapper>
    <component
      :is="editComponent"
      v-if="modalVisible"
      :visible="modalVisible"
      v-bind="editComponentProps"
      @on-update="onUpdated"
      @on-close="modalVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useOrganisationsStore } from "@/store/organisations.store";
import {
  ResellerOrganisationResponse,
  ConsumerOrganisationLicencesResponse,
} from "@/types/licenceSolutions.ts";
import { Organisation } from "@/types/organisatio.ts";
import { isEmbedded } from "@/remote/contract";
import { ACCOUNTS, AuthorisationWrapper, WRITE } from "@/authorisation";
import TUpdateSystems from "@/modals/TUpdateSystems.vue";
import TUpdateMspsIndex from "@/modals/t-update-msps/t-update-msps.index.vue";
import TUpdateCustomer from "@/modals/t-update-customer/t-update-customer.index.vue";
import { TIER } from "@/constants/tier";
import { clearLicencesCache } from "@/utils/licenceApi";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";

interface Props {
  dataTestId: string;
  mspUuid: string;
  rowIndex: number;
  editComponent:
    typeof TUpdateSystems | typeof TUpdateMspsIndex | typeof TUpdateCustomer;
  organisationType: string;
  viewUrl?: string;
  disabled?: boolean;
}

const props = defineProps<Props>();

// Embedding hosts own org editing; hide the remote's row-level edit action so it
// can't mutate accounts outside the host's own management surface.
const embedded = isEmbedded();

const emit = defineEmits(["actionView", "actionEdit", "updated"]);

const loading = ref<boolean>(false);
const modalVisible = ref<boolean>(false);
const mspOrganisationObj = ref<Organisation>({} as Organisation);
const mspLicences = ref<ResellerOrganisationResponse>(
  {} as ResellerOrganisationResponse,
);
const customerLicences = ref<ConsumerOrganisationLicencesResponse>(
  {} as ConsumerOrganisationLicencesResponse,
);

const isCustomer = computed(() => props.organisationType === TIER.customer);

const licenceStore = useLicenceStore();
const organisationsStore = useOrganisationsStore();

const editComponentProps = computed(() => {
  if (props.editComponent === TUpdateCustomer) {
    return {
      orginisationObject: mspOrganisationObj.value,
      listAllLicences: customerLicences.value,
      listLicencesMsp: mspLicences.value,
    };
  }

  return {
    orginisationObject: mspOrganisationObj.value,
    listAllLicences: mspLicences.value,
  };
});

const onUpdated = () => {
  modalVisible.value = false;
  emit("updated");
};

const onEditClick = async () => {
  loading.value = true;
  clearLicencesCache();
  try {
    const response = (await organisationsStore.showOrg({
      uuid: props.mspUuid,
    }))!;
    mspOrganisationObj.value = response.data;
    if (isCustomer.value) {
      customerLicences.value = (await licenceStore.listAllLicences({
        uuid: props.mspUuid,
      })) as ConsumerOrganisationLicencesResponse;
      // Load MSP licences for customer (assuming parent MSP UUID is available)
      mspLicences.value = (await licenceStore.listAllLicences({
        uuid: response.data.parent_uuid,
      })) as ResellerOrganisationResponse;
    } else {
      mspLicences.value = (await licenceStore.listAllLicences({
        uuid: props.mspUuid,
      })) as ResellerOrganisationResponse;
    }
  } catch (_e) {
    loading.value = false;
  } finally {
    modalVisible.value = true;
    loading.value = false;
  }
};
</script>
